/**
 * Parent line choice of the line and N LINES sections (W3-06; DESIGN.md §4.11): a Select for ordinary charts; from
 * `PARENT_SELECT_MAX` lines on, a searchable popover listing at most `PARENT_LIST_MAX` matching lines (a Select
 * renders every option, which took seconds with 2000 lines). Lines that would loop and the line itself are listed
 * disabled with the reason, like the Select's options.
 */

import { useMemo, useState } from 'react';
import type { LineId } from '../../core/model/types';
import { useT } from '../../i18n/useT';
import { Button, Popover, Select, TextField } from '../../ui';
import { m } from './inspector.messages';
import type { ParentOption } from './model';
import s from './Inspector.module.css';

/** Up to this many lines the parent is a plain Select. */
export const PARENT_SELECT_MAX = 150;
/** Most lines the search popover lists at once. */
export const PARENT_LIST_MAX = 50;

export interface ParentPickerProps {
  readonly label: string;
  /** 'none', 'mixed' or the parent's id. */
  readonly value: string;
  readonly options: readonly ParentOption[];
  onChange(parent: LineId | null): void;
}

function optionLabel(o: ParentOption): string {
  return `#${String(o.index)} ${o.name}`;
}

/** Options matching a search ("12", "#12", "main"): by export index or name, in export order. */
export function matchParents(options: readonly ParentOption[], query: string, limit: number): ParentOption[] {
  const q = query.trim().toLowerCase().replace(/^#/, '');
  const out: ParentOption[] = [];
  for (const o of options) {
    if (out.length >= limit) break;
    if (q === '' || String(o.index) === q || o.name.toLowerCase().includes(q) || optionLabel(o).includes(q)) {
      out.push(o);
    }
  }
  return out;
}

export function ParentPicker(props: ParentPickerProps) {
  const t = useT();
  if (props.options.length <= PARENT_SELECT_MAX) {
    return (
      <Select<string>
        ariaLabel={props.label}
        value={props.value}
        options={[
          { value: 'none', label: t(m.parentNone) },
          ...props.options.map((o) => ({
            value: String(o.id),
            label: optionLabel(o),
            ...(o.blocked === 'self'
              ? { disabledReason: { key: m.parentBlockedSelf } }
              : o.blocked === 'loop'
                ? { disabledReason: { key: m.parentBlockedLoop } }
                : {}),
          })),
        ]}
        onChange={(v) => {
          props.onChange(v === 'none' ? null : (Number(v) as LineId));
        }}
      />
    );
  }
  return <LargeParentPicker {...props} />;
}

function LargeParentPicker(props: ParentPickerProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const current = props.options.find((o) => String(o.id) === props.value);
  const shownValue =
    props.value === 'mixed' ? t(m.parentMixed) : current === undefined ? t(m.parentNone) : optionLabel(current);
  const matches = useMemo(() => matchParents(props.options, query, PARENT_LIST_MAX), [props.options, query]);
  const pick = (parent: LineId | null): void => {
    setOpen(false);
    setQuery('');
    props.onChange(parent);
  };
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label={props.label}
      side="bottom"
      align="start"
      contentClassName={s.parentPopover}
      trigger={
        <Button size="sm" variant="secondary" icon="ChevronsUpDown" aria-label={`${props.label}: ${shownValue}`}>
          <span className={s.parentValue}>{shownValue}</span>
        </Button>
      }
    >
      <div
        className={s.parentPicker}
        data-testid="parent-picker"
        onKeyDown={(e) => {
          // Enter in the search box picks the first line that can be picked.
          if (e.key !== 'Enter' || e.nativeEvent.isComposing || !(e.target instanceof HTMLInputElement)) return;
          const first = matches.find((o) => o.blocked === null);
          if (first !== undefined) pick(first.id);
        }}
      >
        <TextField
          label={t(m.parentSearch)}
          value={query}
          autoFocus
          type="search"
          onChange={setQuery}
          onCommit={() => undefined}
        />
        <ul className={s.parentList} aria-label={props.label}>
          <li>
            <button
              type="button"
              className={s.parentItem}
              aria-pressed={props.value === 'none'}
              onClick={() => {
                pick(null);
              }}
            >
              {t(m.parentNone)}
            </button>
          </li>
          {matches.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                className={s.parentItem}
                aria-pressed={String(o.id) === props.value}
                aria-disabled={o.blocked === null ? undefined : true}
                title={
                  o.blocked === 'self'
                    ? t(m.parentBlockedSelf)
                    : o.blocked === 'loop'
                      ? t(m.parentBlockedLoop)
                      : undefined
                }
                onClick={() => {
                  if (o.blocked === null) pick(o.id);
                }}
              >
                {optionLabel(o)}
                {o.blocked !== null && (
                  <span className={s.parentBlocked}>
                    {t(o.blocked === 'self' ? m.parentBlockedSelf : m.parentBlockedLoop)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
        <p className={s.readout}>{t(m.parentShowing, { shown: matches.length, total: props.options.length })}</p>
      </div>
    </Popover>
  );
}
